import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PriorityScoreBar from "@/components/PriorityScoreBar";
import {
  Card, SectionTitle, Pill, ProjectBadge, DueBadge, DEPT_LABELS, Loader, EmptyState,
} from "@/components/common";
import { CalendarRange, TrendingDown, TrendingUp, ListChecks, AlertOctagon, Snowflake, Minus, Mail } from "lucide-react";

export default function WeeklyReview() {
  const { isReadonly, user } = useAuth();
  const [data, setData] = useState(null);
  const [freezing, setFreezing] = useState(false);
  const [digest, setDigest] = useState({ sending: false, msg: "", ok: null });
  const canSendDigest = ["ceo", "admin", "pmo", "commercial"].includes(user?.role);

  const load = useCallback(async () => {
    const { data } = await api.get("/screens/weekly");
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const freeze = async () => {
    setFreezing(true);
    try { await api.post("/snapshots/freeze"); await load(); } finally { setFreezing(false); }
  };

  const sendDigest = async () => {
    setDigest({ sending: true, msg: "", ok: null });
    try {
      const { data } = await api.post("/digest/send");
      setDigest({ sending: false, ok: true, msg: `Digest emailed to ${data.recipients.join(", ")}.` });
    } catch (e) {
      setDigest({ sending: false, ok: false, msg: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  if (!data) return <Loader label="Compiling executive review" />;

  const trend = data.trend;
  const delta = trend.overdue_prev_snapshot == null ? null : trend.overdue_this_week - trend.overdue_prev_snapshot;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight" data-testid="page-title">Weekly Review</h1>
          <p className="text-muted-foreground text-sm mt-1">Executive summary · week of {data.week_start}</p>
        </div>
        <div className="flex items-center gap-3">
          <TrendChip delta={delta} value={trend.overdue_this_week} />
          {canSendDigest && (
            <button onClick={sendDigest} disabled={digest.sending} data-testid="send-digest-btn"
              className="flex items-center gap-2 px-3.5 py-2 rounded-md border border-gold/30 text-gold text-sm hover:bg-gold/10 disabled:opacity-60">
              <Mail size={15} /> {digest.sending ? "Sending…" : "Send digest now"}
            </button>
          )}
          {!isReadonly && (
            <button onClick={freeze} disabled={freezing} data-testid="freeze-week-btn"
              className="flex items-center gap-2 px-3.5 py-2 rounded-md border border-gold/30 text-gold text-sm hover:bg-gold/10 disabled:opacity-60">
              <Snowflake size={15} /> {freezing ? "Freezing…" : "Freeze this week"}
            </button>
          )}
        </div>
      </div>

      {digest.msg && (
        <div data-testid="digest-msg" className={`text-sm rounded-md px-3 py-2 border ${digest.ok ? "text-success bg-success/10 border-success/30" : "text-critical bg-critical/10 border-critical/30"}`}>
          {digest.msg}
        </div>
      )}
      {canSendDigest && (
        <p className="text-xs text-muted-foreground -mt-4 flex items-center gap-1.5">
          <Mail size={12} className="text-gold" /> The digest emails this exact summary to the CEO & GM/PMO. It also sends automatically every Monday.
        </p>
      )}

      {/* Per project */}
      <section>
        <SectionTitle icon={CalendarRange}>Project status</SectionTitle>
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="weekly-project-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="px-4 py-3 font-medium">Project</th>
                  <th className="px-4 py-3 font-medium">This week</th>
                  <th className="px-4 py-3 font-medium">Hours (act / plan)</th>
                  <th className="px-4 py-3 font-medium">Overdue</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.per_project.map((p) => (
                  <tr key={p.id} className="border-b border-border/60 hover:bg-muted/40" data-testid={`weekly-row-${p.job_code}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.job_code} · {p.client_name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full bg-gold" style={{ width: `${p.pct_complete}%` }} />
                        </div>
                        <span className="font-mono-num text-xs">{p.pct_complete}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono-num tabular">
                      {p.actual_hours} / {p.planned_hours}
                    </td>
                    <td className="px-4 py-3">
                      {p.overdue_count > 0
                        ? <span className="text-critical font-mono-num">{p.overdue_count}</span>
                        : <span className="text-muted-foreground">0</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5 flex-wrap">
                        <ProjectBadge badge={p.badge} />
                        {p.compressed && <ProjectBadge compressed />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* Next week must-dos by dept */}
      <section>
        <SectionTitle icon={ListChecks} hint="actionable, due within 7 business days">Next week's must-dos</SectionTitle>
        {Object.keys(data.next_week_by_dept).length === 0 ? (
          <Card className="p-2"><EmptyState title="Nothing due next week" /></Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(data.next_week_by_dept).map(([dept, tasks]) => (
              <Card key={dept} className="p-4" data-testid={`nextweek-${dept}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium">{DEPT_LABELS[dept]}</span>
                  <Pill tone="gold">{tasks.length}</Pill>
                </div>
                <div className="space-y-2.5">
                  {tasks.map((t) => (
                    <div key={t.id} className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate">{t.stage_label} <span className="text-muted-foreground">· {t.project_name}</span></div>
                      </div>
                      <DueBadge daysLeft={t.days_left} overdue={t.overdue} />
                      <div className="w-24"><PriorityScoreBar task={t} height={6} /></div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Slippage */}
      <section>
        <SectionTitle icon={AlertOctagon} hint="passed due date this week, not done">Slippage this week</SectionTitle>
        {data.slippage.length === 0 ? (
          <Card className="p-2"><EmptyState title="No slippage" subtitle="Nothing slipped past its due date this week." /></Card>
        ) : (
          <div className="space-y-2">
            {data.slippage.map((t) => {
              const reason = t.overdue_reason === "Other (specify)" && t.overdue_reason_note
                ? `Other: ${t.overdue_reason_note}` : t.overdue_reason;
              return (
                <Card key={t.id} className="p-3.5 flex items-center justify-between gap-3 border-critical/25" data-testid="slippage-row">
                  <div className="min-w-0">
                    <div>
                      <span className="font-medium">{t.stage_label}</span>
                      <span className="text-muted-foreground text-sm"> · {t.project_name}</span>
                    </div>
                    {reason
                      ? <div className="text-xs text-critical/90 mt-0.5" data-testid="slippage-reason">Reason: {reason}</div>
                      : <div className="text-xs text-muted-foreground/70 mt-0.5">No reason logged</div>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Pill tone="muted">{DEPT_LABELS[t.dept]}</Pill>
                    <Pill tone="critical">{t.days_late}d late</Pill>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function TrendChip({ delta, value }) {
  let Icon = Minus, tone = "muted", txt = "no prior snapshot";
  if (delta != null) {
    if (delta > 0) { Icon = TrendingUp; tone = "critical"; txt = `+${delta} vs last freeze`; }
    else if (delta < 0) { Icon = TrendingDown; tone = "success"; txt = `${delta} vs last freeze`; }
    else { txt = "flat vs last freeze"; }
  }
  return (
    <Pill tone={tone} className="py-1.5 px-3" data-testid="overdue-trend">
      <Icon size={14} /> Overdue {value} · {txt}
    </Pill>
  );
}
